import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useReselector } from '@/utils/sharedHooks'
import {
  GET_ZENDESK_SSO_KEY,
  sendVerificationEmail,
  getZendeskSso,
} from '@/actions/authActions'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { Alert, Button, Text, Loader } from '@/ui'
import { useZendeskSso } from './helpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { isEmailVerified } from '@/selectors/user.selectors'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'
import { VerifyEmailIllustration } from './VerifyEmailIllustration'

const ZendeskSso = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const error = useReselector(getFetchError, GET_ZENDESK_SSO_KEY)
  const isFetching = useReselector(getIsFetching, GET_ZENDESK_SSO_KEY)
  const userEmailVerified = useReselector(isEmailVerified)
  const { zendeskForm, submitForm } = useZendeskSso()
  const [emailSent, setEmailSent] = useState(false)

  useEffect(() => {
    if (!userEmailVerified && !isFetching) {
      track('zendesk message access blocked', {
        reason: 'unverified_email',
      })
    }
  }, [userEmailVerified, isFetching, track])

  useEffect(() => {
    const fetch = async () => {
      if (userEmailVerified) {
        const res = await dispatch(getZendeskSso())
        if (res) {
          submitForm(res.data.jwt)
        }
      }
    }

    fetch()
  }, [dispatch, submitForm, userEmailVerified])

  const handleResendEmail = async () => {
    await dispatch(sendVerificationEmail())
    setEmailSent(true)
  }

  if (!userEmailVerified) {
    return (
      <>
        <Loader active={isFetching} />
        <div className="w-full max-w-[525px] mx-auto text-center pt-12 flex flex-col items-center space-y-6">
          <VerifyEmailIllustration />
          <div className="flex flex-col items-center gap-3 w-full">
            <div className="flex flex-col items-center gap-1">
              <Text variant={TypographyStyles.H1}>
                Please verify your email address
              </Text>
              <Text textAlign="center">
                For security reasons, we require all users to verify their email
                address before accessing messages.
              </Text>
            </div>
            <div className="w-full my-4 p-6 flex flex-col space-y-4">
              {emailSent && (
                <Alert variant="success">
                  <Text>
                    <strong>Verification email sent!</strong>
                  </Text>
                  <Text>
                    Please check your inbox and click the verification link in
                    the email we just sent.
                  </Text>
                </Alert>
              )}
              <div className="flex flex-col items-center space-y-2">
                {!emailSent && (
                  <Button onClick={handleResendEmail}>
                    Send Verification Email
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
          {emailSent && (
            <Text>
              Haven&apos;t received the verification email? Check your spam
              folder or{' '}
              <Button variant="link" onClick={handleResendEmail}>
                click here to send it again
              </Button>
            </Text>
          )}
        </div>
      </>
    )
  }

  if (error) {
    return (
      <Alert variant="error" title="Error">
        <Text>{error?.message}</Text>
        <Button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </Alert>
    )
  }

  return (
    <>
      <div className="centered">
        <Loader />
      </div>
      {zendeskForm}
    </>
  )
}

export default ZendeskSso
